import React, { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { UserProfile } from '@types';
import { adminRoleLabel, adminRolesFor, adminAssignmentError, type Region } from '../common/adminAccess';
import AdminAssignmentFields from './AdminAssignmentFields';
import MfaSecuritySection from './MfaSecuritySection';
import { useMfaDialog } from './useMfaDialog';
import EmailChangeSection from './EmailChangeSection';

interface PlatformAdminProfileProps {
    currentUser: UserProfile;
    onClose: () => void;
    onSaved: (profile: Partial<UserProfile>) => void;
}

const PlatformAdminProfile: React.FC<PlatformAdminProfileProps> = ({ currentUser, onClose, onSaved }) => {
    const [displayName, setDisplayName] = useState(currentUser.displayName || '');
    const [notificationsEnabled, setNotificationsEnabled] = useState(currentUser.scheduledJobNotificationsEnabled !== false);
    const [clientNotificationsEnabled, setClientNotificationsEnabled] = useState(currentUser.clientNotificationsEnabled === true);
    const [saving, setSaving] = useState(false);
    const [securityBusy, setSecurityBusy] = useState(false);
    const [emailBusy, setEmailBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [adminRoles, setAdminRoles] = useState(() => adminRolesFor(currentUser));
    const [regions, setRegions] = useState<Region[]>(currentUser.regions || []);
    const [isTestConsultant, setIsTestConsultant] = useState(currentUser.isTestConsultant === true);
    const close = useRef<HTMLButtonElement>(null);
    const panel = useMfaDialog(onClose, saving || securityBusy || emailBusy);
    useEffect(() => { close.current?.focus(); }, []);

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || securityBusy || emailBusy)
            return;
        const assignmentError = currentUser.adminRole === 'Super' ? adminAssignmentError(adminRoles, regions) : null;
        if (assignmentError) {
            setError(assignmentError);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            const data: Record<string, unknown> = { displayName };
            if (currentUser.adminRole === 'Super') {
                data.scheduledJobNotificationsEnabled = notificationsEnabled;
                data.adminRoles = adminRoles;
                data.regions = regions;
                data.isTestConsultant = adminRoles.includes('Regional Consultant') && isTestConsultant;
            }
            if (currentUser.adminRole === 'Super' || currentUser.adminRole === 'Global')
                data.clientNotificationsEnabled = clientNotificationsEnabled;
            const response = await callable({ action: 'updateOwnAdminProfile', data });
            const result = response.data as { profile: Partial<UserProfile> };
            onSaved(result.profile);
            onClose();
        } catch (saveError: any) {
            setError(saveError.message || 'Profile update failed.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
            <section ref={panel} role="dialog" aria-modal="true" aria-labelledby="admin-profile-title" className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 text-slate-900 shadow-2xl [color-scheme:light]">
                <div className="mb-5 flex items-center justify-between gap-3">
                    <div>
                        <h2 id="admin-profile-title" className="text-xl font-extrabold text-slate-900">Admin Profile</h2>
                        <p className="text-xs text-slate-500">Manage your management portal profile.</p>
                    </div>
                    <button ref={close} type="button" disabled={saving || securityBusy || emailBusy} onClick={onClose} className="rounded-lg px-3 py-1 text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50" aria-label="Close profile">×</button>
                </div>
                {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}
                <form onSubmit={save} className="space-y-4">
                    <label className="block">
                        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Display Name</span>
                        <input value={displayName} onChange={event => setDisplayName(event.target.value)} maxLength={100} required className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                    </label>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <label className="block">
                            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Email</span>
                            <input value={currentUser.email} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Admin Level</span>
                            <input value={adminRoleLabel(currentUser.adminRole)} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
                        </label>
                    </div>
                    <AdminAssignmentFields adminRoles={adminRoles} regions={regions} disabled={currentUser.adminRole !== 'Super' || saving || securityBusy || emailBusy} onChange={(selectedRoles, selectedRegions) => { setAdminRoles(selectedRoles); setRegions(selectedRegions); setIsTestConsultant(selectedRoles.includes('Regional Consultant') && isTestConsultant); }} />
                    {currentUser.adminRole === 'Super' && adminRoles.includes('Regional Consultant') && <label className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-950"><input type="checkbox" checked={isTestConsultant} disabled={saving || securityBusy || emailBusy} onChange={event => setIsTestConsultant(event.target.checked)} />Test Consultant<span className="font-normal">Exclude from automatic MSA assignment. Manual assignment remains available.</span></label>}
                    {currentUser.organizationId && (
                        <label className="block">
                            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Organization ID</span>
                            <input value={currentUser.organizationId} disabled className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
                        </label>
                    )}
                    {currentUser.adminRole === 'Super' && (
                        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <input type="checkbox" checked={notificationsEnabled} onChange={event => setNotificationsEnabled(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                            <span>
                                <span className="block text-sm font-bold text-slate-800">Scheduled job failure alerts</span>
                                <span className="mt-1 block text-xs leading-5 text-slate-500">Email me when scheduled jobs encounter major errors.</span>
                            </span>
                        </label>
                    )}
                    {(currentUser.adminRole === 'Super' || currentUser.adminRole === 'Global') && (
                        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <input type="checkbox" checked={clientNotificationsEnabled} onChange={event => setClientNotificationsEnabled(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                            <span>
                                <span className="block text-sm font-bold text-slate-800">All client notifications</span>
                                <span className="mt-1 block text-xs leading-5 text-slate-500">Receive email and in-app alerts for every client's chats, tickets, service requests, and updates sent to their assigned consultant.</span>
                            </span>
                        </label>
                    )}
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" disabled={saving || securityBusy || emailBusy} onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
                        <button disabled={saving || securityBusy || emailBusy} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save Profile'}</button>
                    </div>
                </form>
                <section aria-labelledby="admin-profile-security-title" className="mt-6 space-y-4 border-t border-slate-200 pt-5">
                    <h3 id="admin-profile-security-title" className="text-lg font-bold">Account security</h3>
                    <MfaSecuritySection disabled={saving || emailBusy} onBusyChange={setSecurityBusy} />
                    <EmailChangeSection disabled={saving || securityBusy} onBusyChange={setEmailBusy} />
                </section>
            </section>
        </div>
    );
};

export default PlatformAdminProfile;
